import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  getOrganizations, createOrganization, setOrgStatus, setOrgNotes, deleteOrganization,
  getBillingPlans, updateBillingPlan, setOrgPlan,
  getAdminInvoices, createInvoice, updateInvoice, setInvoiceStatus, deleteInvoice, openInvoicePdf,
  getPlatformBackups, runPlatformBackup, getPlatformBackupStatus, restorePlatformBackup, restoreTenantBackup, downloadPlatformBackup, fmt,
} from "@/lib/api";
import { StatusBadge } from "@/pages/Billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PlatformUsageTab } from "@/pages/superadmin/PlatformUsageTab";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import {
  Building2, Plus, Power, PowerOff, LogOut, Loader2, Users, Download, Trash2,
  Send, CheckCircle2, Pencil, FileText, AlertTriangle,
} from "lucide-react";

const money = (n, cur) => `${Number(n || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || "RON"}`;
const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ description: "", qty: 1, unit_price: 0 });

/* ------------------------- Invoice create/edit dialog ------------------------- */
const InvoiceDialog = ({ open, onOpenChange, orgs, plans, invoice, presetOrgId, onSaved }) => {
  const { t } = useT();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (invoice) {
      setForm({
        org_id: invoice.org_id, plan_id: invoice.plan_id || "none", currency: invoice.currency || "RON",
        issue_date: invoice.issue_date || today(), due_date: invoice.due_date || today(),
        period_start: invoice.period_start || "", period_end: invoice.period_end || "",
        lines: (invoice.lines && invoice.lines.length ? invoice.lines : [emptyLine()]).map((l) => ({ ...l })),
        notes: invoice.notes || "", status: invoice.status || "draft",
      });
    } else {
      setForm({
        org_id: presetOrgId || (orgs[0]?.id ?? ""), plan_id: "none", currency: "RON",
        issue_date: today(), due_date: today(), period_start: "", period_end: "",
        lines: [emptyLine()], notes: "", status: "draft",
      });
    }
  }, [open, invoice, presetOrgId, orgs]);

  if (!form) return null;
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => {
    const lines = f.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l));
    return { ...f, lines };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const total = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        org_id: form.org_id,
        plan_id: form.plan_id === "none" ? null : form.plan_id,
        currency: form.currency,
        issue_date: form.issue_date, due_date: form.due_date,
        period_start: form.period_start || null, period_end: form.period_end || null,
        lines: form.lines
          .filter((l) => (l.description || "").trim())
          .map((l) => ({ description: l.description, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })),
        notes: form.notes,
      };
      if (invoice) {
        await updateInvoice(invoice.id, payload);
        toast.success(t("billing.updatedToast"));
      } else {
        await createInvoice({ ...payload, status: form.status });
        toast.success(t("billing.createdToast"));
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.actionError"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle>{invoice ? t("billing.edit") : t("billing.create")}</DialogTitle></DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("billing.org")}</Label>
              <Select value={form.org_id} onValueChange={(v) => setF("org_id", v)} disabled={!!invoice}>
                <SelectTrigger data-testid="invoice-org-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("billing.plan")}</Label>
              <Select value={form.plan_id} onValueChange={(v) => setF("plan_id", v)}>
                <SelectTrigger data-testid="invoice-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("billing.currency")}</Label>
              <Select value={form.currency} onValueChange={(v) => setF("currency", v)}>
                <SelectTrigger data-testid="invoice-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RON">RON</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!invoice && (
              <div className="space-y-1.5">
                <Label>{t("billing.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setF("status", v)}>
                  <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t("billing.statusDraft")}</SelectItem>
                    <SelectItem value="sent">{t("billing.statusSent")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("billing.issueDate")}</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setF("issue_date", e.target.value)} data-testid="invoice-issue-date" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("billing.dueDate")}</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setF("due_date", e.target.value)} data-testid="invoice-due-date" />
            </div>
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("billing.lines")}</Label>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="invoice-add-line"><Plus className="mr-1.5 h-3.5 w-3.5" />{t("billing.addLine")}</Button>
            </div>
            {form.lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`invoice-line-${i}`}>
                <Input className="flex-1" placeholder={t("billing.description")} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} />
                <Input className="w-20 text-right" type="number" placeholder={t("billing.qty")} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`invoice-line-qty-${i}`} />
                <Input className="w-28 text-right" type="number" placeholder={t("billing.unitPrice")} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`invoice-line-price-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeLine(i)} data-testid={`invoice-line-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
              </div>
            ))}
            <div className="flex justify-end pt-1 text-sm font-semibold" data-testid="invoice-total">
              {t("billing.total")}: {money(total, form.currency)}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("billing.notes")}</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} data-testid="invoice-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="invoice-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("billing.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------- Platform backups panel ------------------------- */
function BackupsPanel() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null); // backup being restored
  const [confirmText, setConfirmText] = useState("");
  const [scope, setScope] = useState("__platform__"); // "__platform__" or an org id
  const [orgs, setOrgs] = useState([]);

  const load = async () => {
    setLoading(true);
    try { setData(await getPlatformBackups()); } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); getOrganizations().then((d) => setOrgs(Array.isArray(d) ? d : (d?.organizations || []))).catch(() => {}); }, []);

  // Poll job status while a backup/restore is running.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await getPlatformBackupStatus();
        if (!alive) return;
        setStatus(s);
        if (s?.state === "running") setTimeout(tick, 3000);
        else if (s?.state === "done") load();
      } catch { /* ignore */ }
    };
    tick();
    return () => { alive = false; };
  }, [busy]);

  const backupNow = async () => {
    setBusy(true);
    try { await runPlatformBackup(); toast.success(t("backups.startedBackup")); setBusy((b) => !b); }
    catch (e) { toast.error(e?.response?.data?.detail || t("backups.failed")); }
    finally { setBusy(false); }
  };

  const doRestore = async () => {
    if ((confirmText || "").trim().toUpperCase() !== "RESTORE") return;
    try {
      if (scope === "__platform__") {
        await restorePlatformBackup(confirmFor.id, confirmText.trim());
      } else {
        await restoreTenantBackup(confirmFor.id, scope, confirmText.trim());
      }
      toast.success(t("backups.startedRestore"));
      setConfirmFor(null); setConfirmText(""); setBusy((b) => !b);
    } catch (e) { toast.error(e?.response?.data?.detail || t("backups.failed")); }
  };

  const openRestore = (b) => { setConfirmFor(b); setConfirmText(""); setScope("__platform__"); };

  const kindBadge = (k) => {
    const cls = k === "safety" ? "bg-[hsl(38_92%_50%/0.15)] text-[hsl(38_92%_40%)]"
      : k === "manual" ? "bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]"
      : "bg-secondary text-muted-foreground";
    return <Badge variant="secondary" className={cls} data-testid={`backup-kind-${k}`}>{t(`backups.kind_${k}`)}</Badge>;
  };

  const rows = data?.backups || [];
  const sched = data?.schedule || {};
  const running = status?.state === "running";

  return (
    <div className="space-y-4" data-testid="backups-panel">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <div className="text-sm font-medium">{t("backups.autoTitle")}</div>
            <p className="text-xs text-muted-foreground">
              {t("backups.autoHint", { hour: String(sched.hour_utc ?? 2).padStart(2, "0"), keep: sched?.retention?.auto ?? 7 })}
            </p>
            {running && (
              <p className="mt-1 text-xs font-medium text-[hsl(var(--brand))]" data-testid="backup-status-running">
                {status?.action === "restore" ? t("backups.restoring") : t("backups.backingUp")} — {status?.message}
              </p>
            )}
          </div>
          <Button onClick={backupNow} disabled={busy || running} data-testid="backup-now-btn">
            {t("backups.backupNow")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("backups.listTitle")}</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="backups-empty">{t("backups.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="backups-table">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">{t("backups.colWhen")}</th>
                    <th className="py-2 pr-3">{t("backups.colType")}</th>
                    <th className="py-2 pr-3 text-right">{t("backups.colRecords")}</th>
                    <th className="py-2 pr-3 text-right">{t("backups.colFiles")}</th>
                    <th className="py-2 pr-3 text-right">{t("backups.colSize")}</th>
                    <th className="py-2 pr-3 text-right">{t("backups.colActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id} className="border-b border-border/60" data-testid={`backup-row-${b.id}`}>
                      <td className="py-2 pr-3 font-mono text-xs">{new Date(b.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-3">{kindBadge(b.kind)}</td>
                      <td className="py-2 pr-3 text-right font-mono">{fmt(b.documents, 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono">{fmt(b.files, 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono">{(b.size / 1024 / 1024).toFixed(2)} MB</td>
                      <td className="py-2 pr-3">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => downloadPlatformBackup(b.id).catch(() => toast.error(t("backups.failed")))} data-testid={`backup-download-${b.id}`}>
                            {t("backups.download")}
                          </Button>
                          <Button size="sm" variant="outline" className="border-[hsl(var(--danger)/0.4)] text-[hsl(var(--danger))]"
                            disabled={running}
                            onClick={() => openRestore(b)} data-testid={`backup-restore-${b.id}`}>
                            {t("backups.restore")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmFor} onOpenChange={(v) => { if (!v) { setConfirmFor(null); setConfirmText(""); } }}>
        <DialogContent data-testid="restore-confirm-dialog">
          <DialogHeader><DialogTitle>{t("backups.restoreTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("backups.restoreScope")}</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="restore-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__platform__">{t("backups.scopePlatform")}</SelectItem>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`restore-scope-${o.id}`}>
                      {o.name || o.slug || o.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              {scope === "__platform__"
                ? (confirmFor && t("backups.restoreWarn", { when: new Date(confirmFor.created_at).toLocaleString() }))
                : (confirmFor && t("backups.restoreWarnTenant", {
                    tenant: (orgs.find((o) => o.id === scope)?.name) || scope,
                    when: new Date(confirmFor.created_at).toLocaleString(),
                  }))}
            </p>
            <p className="text-sm">{t("backups.restoreSafety")}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("backups.restoreConfirmLabel")}</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="RESTORE" data-testid="restore-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmFor(null); setConfirmText(""); }} data-testid="restore-cancel">{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--danger))] text-white hover:opacity-90"
              disabled={confirmText.trim().toUpperCase() !== "RESTORE"}
              onClick={doRestore} data-testid="restore-confirm-btn">{t("backups.restoreConfirmBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ------------------------- Main SuperAdmin page ------------------------- */
export default function SuperAdmin() {
  const { user, ready, signOut } = useAuth();
  const { t } = useT();
  const [orgs, setOrgs] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [invDialog, setInvDialog] = useState({ open: false, invoice: null, presetOrgId: null });
  const [planDraft, setPlanDraft] = useState({});
  const [delOrg, setDelOrg] = useState(null);      // org pending deletion (opens confirm dialog)
  const [delConfirm, setDelConfirm] = useState(""); // typed company-name confirmation
  const [deleting, setDeleting] = useState(false);

  const loadOrgs = async () => {
    try { setOrgs(await getOrganizations()); }
    catch { toast.error(t("superadmin.loadError")); setOrgs([]); }
  };
  const loadPlans = async () => {
    try { const p = await getBillingPlans(); setPlans(p); const d = {}; p.forEach((x) => { d[x.id] = { ...x }; }); setPlanDraft(d); }
    catch (e) { console.error("Failed to load billing plans (non-fatal):", e); }
  };
  const loadInvoices = async () => {
    try { setInvoices(await getAdminInvoices()); }
    catch { toast.error(t("billing.loadError")); setInvoices([]); }
  };

  useEffect(() => {
    if (user?.role === "superadmin") { loadOrgs(); loadPlans(); loadInvoices(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const stats = useMemo(() => {
    const list = orgs || [];
    return { total: list.length, active: list.filter((o) => o.status === "active").length,
      suspended: list.filter((o) => o.status === "suspended").length };
  }, [orgs]);

  const filteredInvoices = useMemo(() => {
    const list = invoices || [];
    return statusFilter === "all" ? list : list.filter((i) => i.status === statusFilter);
  }, [invoices, statusFilter]);

  if (ready && (!user || user.role !== "superadmin")) return <Navigate to="/login" replace />;

  const toggle = async (o) => {
    const next = o.status === "active" ? "suspended" : "active";
    try { await setOrgStatus(o.id, next); toast.success(t("superadmin.statusToast")); loadOrgs(); }
    catch { toast.error(t("superadmin.actionError")); }
  };
  const saveNotes = async (o, notes) => {
    try { await setOrgNotes(o.id, notes); } catch { toast.error(t("superadmin.actionError")); }
  };
  const assignPlan = async (o, planId) => {
    try { await setOrgPlan(o.id, planId); toast.success(t("superadmin.statusToast")); loadOrgs(); }
    catch { toast.error(t("superadmin.actionError")); }
  };
  const createOrg = async () => {
    setSaving(true);
    try {
      await createOrganization({ ...form, email: form.email.trim().toLowerCase() });
      toast.success(t("superadmin.createdToast"));
      setAddOpen(false);
      setForm({ company_name: "", admin_name: "", email: "", password: "" });
      loadOrgs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("superadmin.actionError"));
    } finally { setSaving(false); }
  };
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const deleteOrg = async () => {
    if (!delOrg) return;
    setDeleting(true);
    try {
      await deleteOrganization(delOrg.id);
      toast.success(t("superadmin.deletedToast", { name: delOrg.company_name }));
      setDelOrg(null);
      setDelConfirm("");
      loadOrgs();
      loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("superadmin.actionError"));
    } finally {
      setDeleting(false);
    }
  };

  const changeInvoiceStatus = async (inv, status) => {
    try { await setInvoiceStatus(inv.id, status); toast.success(t("billing.statusToast")); loadInvoices(); loadOrgs(); }
    catch { toast.error(t("billing.actionError")); }
  };
  const removeInvoice = async (inv) => {
    if (!window.confirm(t("billing.confirmDelete"))) return;
    try { await deleteInvoice(inv.id); toast.success(t("billing.deletedToast")); loadInvoices(); loadOrgs(); }
    catch { toast.error(t("billing.actionError")); }
  };
  const downloadPdf = async (inv) => {
    try { await openInvoicePdf(`/admin/invoices/${inv.id}/pdf`); }
    catch { toast.error(t("billing.actionError")); }
  };
  const savePlan = async (id) => {
    const d = planDraft[id];
    try {
      await updateBillingPlan(id, {
        name: d.name, price_ron: Number(d.price_ron) || 0, price_eur: Number(d.price_eur) || 0,
        interval: d.interval || "monthly", features: d.features || "", active: d.active !== false,
        milk_limit_l: Number(d.milk_limit_l) || 0,
      });
      toast.success(t("billing.plansSaved"));
      loadPlans();
    } catch { toast.error(t("billing.actionError")); }
  };
  const setPlanF = (id, k, v) => setPlanDraft((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const orgName = (id) => (orgs || []).find((o) => o.id === id)?.company_name || id;

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand))] text-white">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">{t("superadmin.title")}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="outline" size="sm" onClick={signOut} data-testid="superadmin-logout">
              <LogOut className="mr-2 h-4 w-4" /> {t("userMenu.signOut")}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6" data-testid="superadmin-page">
        <div className="grid grid-cols-3 gap-4">
          {[["superadmin.total", stats.total], ["superadmin.active", stats.active], ["superadmin.suspended", stats.suspended]].map(([k, v]) => (
            <Card key={k}><CardContent className="p-4"><div className="text-2xl font-semibold">{v}</div><div className="text-xs text-muted-foreground">{t(k)}</div></CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="orgs">
          <TabsList data-testid="superadmin-tabs">
            <TabsTrigger value="orgs" data-testid="tab-orgs">{t("superadmin.tabOrgs")}</TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage">{t("usage.tab")}</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">{t("superadmin.tabInvoices")}</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">{t("superadmin.tabPlans")}</TabsTrigger>
            <TabsTrigger value="backups" data-testid="tab-backups">{t("backups.tab")}</TabsTrigger>
          </TabsList>

          {/* ------------------------- Usage / adoption ------------------------- */}
          <TabsContent value="usage" className="mt-4">
            <PlatformUsageTab />
          </TabsContent>

          {/* ------------------------- Organizations ------------------------- */}
          <TabsContent value="orgs" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">{t("superadmin.orgs")}</CardTitle>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="superadmin-add-org"><Plus className="mr-2 h-4 w-4" />{t("superadmin.addOrg")}</Button>
                  </DialogTrigger>
                  <DialogContent data-testid="superadmin-add-dialog">
                    <DialogHeader><DialogTitle>{t("superadmin.addOrg")}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-1.5"><Label>{t("signup.company")}</Label><Input value={form.company_name} onChange={setF("company_name")} data-testid="superadmin-org-company" /></div>
                      <div className="space-y-1.5"><Label>{t("signup.adminName")}</Label><Input value={form.admin_name} onChange={setF("admin_name")} data-testid="superadmin-org-admin" /></div>
                      <div className="space-y-1.5"><Label>{t("signup.email")}</Label><Input type="email" value={form.email} onChange={setF("email")} data-testid="superadmin-org-email" /></div>
                      <div className="space-y-1.5"><Label>{t("signup.password")}</Label><Input type="password" value={form.password} onChange={setF("password")} data-testid="superadmin-org-password" /></div>
                    </div>
                    <DialogFooter>
                      <Button onClick={createOrg} disabled={saving} data-testid="superadmin-org-create">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("superadmin.create")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {orgs === null ? (
                  <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
                ) : orgs.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">{t("superadmin.empty")}</p>
                ) : (
                  <div className="space-y-3">
                    {orgs.map((o) => (
                      <div key={o.id} className="flex flex-col gap-3 rounded-lg border border-border p-4 lg:flex-row lg:items-center lg:justify-between" data-testid={`superadmin-org-${o.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{o.company_name}</span>
                            {o.status === "active"
                              ? <Badge className="badge-ok" data-testid={`org-status-${o.id}`}>{t("superadmin.active")}</Badge>
                              : <Badge className="badge-bad" data-testid={`org-status-${o.id}`}>{t("superadmin.suspended")}</Badge>}
                            {o.billing_locked && <Badge className="badge-bad" data-testid={`org-billing-${o.id}`}>{t("superadmin.billingLocked")}</Badge>}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span>{o.admin_email}</span>
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{o.user_count}</span>
                            {o.overdue_count > 0 && <span className="text-[hsl(var(--danger))]">{o.overdue_count} {t("superadmin.overdue")}</span>}
                            {o.outstanding > 0 && <span>{t("superadmin.outstanding")}: {money(o.outstanding)}</span>}
                          </div>
                          <Input
                            className="mt-2 h-8 text-xs"
                            placeholder={t("superadmin.notesPh")}
                            defaultValue={o.notes || ""}
                            onBlur={(e) => saveNotes(o, e.target.value)}
                            data-testid={`org-notes-${o.id}`}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Select value={o.plan_id || ""} onValueChange={(v) => assignPlan(o, v)}>
                            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`org-plan-${o.id}`}>
                              <SelectValue placeholder={t("superadmin.assignPlan")} />
                            </SelectTrigger>
                            <SelectContent>
                              {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" onClick={() => setInvDialog({ open: true, invoice: null, presetOrgId: o.id })} data-testid={`org-new-invoice-${o.id}`}>
                            <FileText className="mr-1.5 h-3.5 w-3.5" />{t("superadmin.newInvoice")}
                          </Button>
                          <Button
                            variant={o.status === "active" ? "outline" : "default"}
                            size="sm"
                            onClick={() => toggle(o)}
                            data-testid={`org-toggle-${o.id}`}
                            className={o.status === "active" ? "text-[hsl(var(--danger))]" : ""}
                          >
                            {o.status === "active" ? <><PowerOff className="mr-2 h-4 w-4" />{t("superadmin.suspendBtn")}</> : <><Power className="mr-2 h-4 w-4" />{t("superadmin.activateBtn")}</>}
                          </Button>
                          {!o.is_primary && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setDelOrg(o); setDelConfirm(""); }}
                              data-testid={`org-delete-${o.id}`}
                              className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--qc-bad-bg))]"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />{t("superadmin.deleteBtn")}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Delete-tenant confirmation — irreversible, requires typing the company name */}
            <Dialog open={!!delOrg} onOpenChange={(o) => { if (!o) { setDelOrg(null); setDelConfirm(""); } }}>
              <DialogContent data-testid="superadmin-delete-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-[hsl(var(--danger))]">
                    <AlertTriangle className="h-5 w-5" />{t("superadmin.deleteTitle")}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground" data-testid="superadmin-delete-warning">
                    {t("superadmin.deleteWarning", { name: delOrg?.company_name || "" })}
                  </p>
                  <div className="space-y-1.5">
                    <Label>{t("superadmin.deleteConfirmLabel", { name: delOrg?.company_name || "" })}</Label>
                    <Input
                      value={delConfirm}
                      onChange={(e) => setDelConfirm(e.target.value)}
                      placeholder={delOrg?.company_name || ""}
                      data-testid="superadmin-delete-confirm-input"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDelOrg(null); setDelConfirm(""); }} disabled={deleting} data-testid="superadmin-delete-cancel">
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={deleteOrg}
                    disabled={deleting || delConfirm.trim() !== (delOrg?.company_name || "").trim()}
                    data-testid="superadmin-delete-confirm"
                    className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                  >
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    {t("superadmin.deleteBtn")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
          <TabsContent value="invoices" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">{t("billing.invoices")}</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("billing.filterAll")}</SelectItem>
                      <SelectItem value="draft">{t("billing.statusDraft")}</SelectItem>
                      <SelectItem value="sent">{t("billing.statusSent")}</SelectItem>
                      <SelectItem value="paid">{t("billing.statusPaid")}</SelectItem>
                      <SelectItem value="overdue">{t("billing.statusOverdue")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => setInvDialog({ open: true, invoice: null, presetOrgId: null })} data-testid="new-invoice-btn">
                    <Plus className="mr-2 h-4 w-4" />{t("billing.create")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {invoices === null ? (
                  <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
                ) : filteredInvoices.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">{t("billing.empty")}</p>
                ) : (
                  <Table data-testid="admin-invoices-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("billing.number")}</TableHead>
                        <TableHead>{t("billing.org")}</TableHead>
                        <TableHead>{t("billing.dueDate")}</TableHead>
                        <TableHead className="text-right">{t("billing.amount")}</TableHead>
                        <TableHead>{t("billing.status")}</TableHead>
                        <TableHead className="text-right">{t("billing.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((inv) => (
                        <TableRow key={inv.id} data-testid={`admin-invoice-${inv.id}`}>
                          <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                          <TableCell className="text-sm">{orgName(inv.org_id)}</TableCell>
                          <TableCell className="text-sm">{inv.due_date}</TableCell>
                          <TableCell className="text-right font-medium">{money(inv.amount, inv.currency)}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {inv.status === "draft" && (
                                <Button variant="ghost" size="icon" title={t("billing.markSent")} onClick={() => changeInvoiceStatus(inv, "sent")} data-testid={`invoice-send-${inv.id}`}>
                                  <Send className="h-4 w-4 text-[hsl(var(--brand))]" />
                                </Button>
                              )}
                              {(inv.status === "sent" || inv.status === "overdue") && (
                                <Button variant="ghost" size="icon" title={t("billing.markPaid")} onClick={() => changeInvoiceStatus(inv, "paid")} data-testid={`invoice-paid-${inv.id}`}>
                                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" title={t("billing.downloadPdf")} onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.id}`}>
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title={t("billing.edit")} onClick={() => setInvDialog({ open: true, invoice: inv, presetOrgId: null })} data-testid={`invoice-edit-${inv.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title={t("billing.delete")} onClick={() => removeInvoice(inv)} data-testid={`invoice-delete-${inv.id}`}>
                                <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ------------------------- Plans ------------------------- */}
          <TabsContent value="plans" className="mt-4">
            <div className="grid gap-4 sm:grid-cols-3">
              {plans.map((p) => {
                const d = planDraft[p.id] || p;
                return (
                  <Card key={p.id} data-testid={`plan-card-${p.code}`}>
                    <CardHeader className="pb-2"><CardTitle className="text-base">{p.name}</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1.5"><Label className="text-xs">{t("billing.planName")}</Label>
                        <Input value={d.name || ""} onChange={(e) => setPlanF(p.id, "name", e.target.value)} data-testid={`plan-name-${p.code}`} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">{t("billing.priceRon")}</Label>
                        <Input type="number" value={d.price_ron ?? 0} onChange={(e) => setPlanF(p.id, "price_ron", e.target.value)} data-testid={`plan-ron-${p.code}`} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">{t("billing.priceEur")}</Label>
                        <Input type="number" value={d.price_eur ?? 0} onChange={(e) => setPlanF(p.id, "price_eur", e.target.value)} data-testid={`plan-eur-${p.code}`} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">{t("billing.milkLimit")}</Label>
                        <Input type="number" value={d.milk_limit_l ?? 0} onChange={(e) => setPlanF(p.id, "milk_limit_l", e.target.value)} data-testid={`plan-milk-${p.code}`} />
                        <p className="text-[11px] text-muted-foreground">{t("billing.milkLimitHint")}</p></div>
                      <div className="space-y-1.5"><Label className="text-xs">{t("billing.features")}</Label>
                        <Textarea rows={2} value={d.features || ""} onChange={(e) => setPlanF(p.id, "features", e.target.value)} data-testid={`plan-features-${p.code}`} /></div>
                      <Button size="sm" className="w-full" onClick={() => savePlan(p.id)} data-testid={`plan-save-${p.code}`}>{t("billing.savePlan")}</Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* ------------------------- Backups ------------------------- */}
          <TabsContent value="backups" className="mt-4">
            <BackupsPanel />
          </TabsContent>
        </Tabs>
      </main>

      <InvoiceDialog
        open={invDialog.open}
        onOpenChange={(v) => setInvDialog((s) => ({ ...s, open: v }))}
        orgs={orgs || []}
        plans={plans}
        invoice={invDialog.invoice}
        presetOrgId={invDialog.presetOrgId}
        onSaved={() => { loadInvoices(); loadOrgs(); }}
      />
    </div>
  );
}
